import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from '@mui/material'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { UITooltipHelpIcon } from 'src/components/HelpIcon'
import { ValueSliderWithInput } from 'src/components/ui/Slider'
import { UISpinnerLoading } from 'src/components/ui/SpinnerLoading'
import type { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useDynamicConstants } from 'src/services/constants'
import { cx } from 'src/utils/cx'
import { isSD3Model } from 'src/utils/model'
import { SD3_COMPAT_SAMPLING_METHODS } from 'src/utils/task'
import type { HandleChange } from 'src/utils/useForm'
import type { TaskSubmitFormValues } from '../../BaseTaskSubmitForm'
import { DEFAULT_SAMPLING_METHOD_IF_EMPTY } from '../../constants'
import {
  ControlSectionMessage,
  ControlSectionMessageType,
} from './ControlSection'

const MIN_CFG_SCALE = 1.1
const MAX_CFG_SCALE = 15
const CFG_SCALE_STEP = 0.1

const SHIFT_VALUE_STEP = 0.1
const MIN_SHIFT_VALUE = 0.8
const MAX_SHIFT_VALUE = 15

const MIN_SAMPLING_STEPS = 1
const MAX_SAMPLING_STEPS = 50
const SAMPLING_STEPS_STEP = 1

function Spinner() {
  return (
    <div className="w-full flex justify-center items-center h-20">
      <UISpinnerLoading className="size-10" loading></UISpinnerLoading>
    </div>
  )
}

export function SamplingStepSelectInner({
  value,
  handleChange,
  onChange,
  sliderClassName,
  lightningMode,
  disabled,
  minSamplingSteps = MIN_SAMPLING_STEPS,
  maxSamplingSteps = MAX_SAMPLING_STEPS,
}: {
  value?: number
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (value: number) => void
  sliderClassName?: string
  lightningMode?: boolean
  disabled?: boolean
  minSamplingSteps?: number
  maxSamplingSteps?: number
}) {
  const { t } = useTranslation(['generation'])

  const changeHandler = useCallback(
    (val: number | number[]) => {
      handleChange
        ? handleChange('samplingSteps')(val)
        : onChange?.(val as number)
    },
    [handleChange, onChange],
  )

  return (
    <>
      {lightningMode && (
        <ControlSectionMessage
          className="my-2"
          type={ControlSectionMessageType.Warning}
        >
          {t('generation:sampling-steps.unavailable-in-lightning-mode')}
        </ControlSectionMessage>
      )}
      {value == null ? (
        <Spinner />
      ) : (
        <ValueSliderWithInput
          isInteger
          value={value}
          sliderClassName={cx('max-w-[400px]', sliderClassName)}
          disabled={disabled}
          onChange={value => changeHandler(value)}
          min={minSamplingSteps}
          max={maxSamplingSteps}
          step={SAMPLING_STEPS_STEP}
        />
      )}
    </>
  )
}

export function SamplingStepSelect({
  value,
  handleChange,
  onChange,
  sliderClassName,
  lightningMode,
  disabled,
}: {
  value?: number
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (value: number) => void
  sliderClassName?: string
  lightningMode?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])
  const helpIcon = (
    <UITooltipHelpIcon variant="normal" TooltipProps={{ placement: 'start' }}>
      <p className="text-sm w-80 whitespace-pre-wrap">
        {t('generation:sampling-steps.help')}
      </p>
    </UITooltipHelpIcon>
  )

  return (
    <div>
      <div className="flex gap-2 items-center mb-2">
        <h2 className="text-[14px]">{t('generation:sampling-steps.label')}</h2>
        {helpIcon}
      </div>
      <SamplingStepSelectInner
        value={value}
        handleChange={handleChange}
        onChange={onChange}
        sliderClassName={sliderClassName}
        lightningMode={lightningMode}
        disabled={disabled}
      />
    </div>
  )
}

export function SamplingMethodsSelectInner({
  value = DEFAULT_SAMPLING_METHOD_IF_EMPTY,
  handleChange,
  onChange,
  selectClassName,
  disabled,
  modelType,
  hideLabel,
  allowedSamplingMethods,
}: {
  value?: string
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (event: SelectChangeEvent<string>) => void
  selectClassName?: string
  disabled?: boolean
  modelType?: GenerationModelType
  hideLabel?: boolean
  // TODO: implement disallowed sampling methods
  allowedSamplingMethods?: string[]
}) {
  const { t } = useTranslation(['generation'])
  const { data: dynamicConstants, isValidating: dynamicConstantsLoading } =
    useDynamicConstants()

  const usingSd3Model = isSD3Model(modelType)
  const samplingMethods = useMemo(() => {
    if (
      Array.isArray(allowedSamplingMethods) &&
      allowedSamplingMethods.length > 0
    )
      return allowedSamplingMethods

    const dynamicModels =
      dynamicConstants?.samplingMethods?.filter(m =>
        usingSd3Model ? SD3_COMPAT_SAMPLING_METHODS.includes(m) : true,
      ) ?? []

    return dynamicModels
  }, [allowedSamplingMethods, dynamicConstants?.samplingMethods, usingSd3Model])

  return (
    <FormControl fullWidth>
      {!hideLabel && (
        <InputLabel size="small">
          {t('generation:sampling-method.label')}
        </InputLabel>
      )}
      {dynamicConstantsLoading && samplingMethods.length < 1 ? (
        <Spinner />
      ) : (
        <Select
          disabled={disabled}
          size="small"
          className={cx('max-w-[500px]', selectClassName)}
          value={value}
          label={hideLabel ? undefined : t('generation:sampling-method.label')}
          onChange={handleChange ? handleChange('samplingMethod') : onChange}
        >
          <MenuItem value="" disabled>
            <em className="opacity-50">
              {t('generation:vae-model.select-placeholder')}
            </em>
          </MenuItem>
          {samplingMethods.map(key => {
            return (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            )
          })}
        </Select>
      )}
    </FormControl>
  )
}

export function SamplingMethodsSelect({
  value = DEFAULT_SAMPLING_METHOD_IF_EMPTY,
  handleChange,
  onChange,
  selectClassName,
  disabled,
  modelType,
}: {
  value?: string
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (event: SelectChangeEvent<string>) => void
  selectClassName?: string
  disabled?: boolean
  modelType?: GenerationModelType
}) {
  const { t } = useTranslation(['generation'])

  const helpIcon = (
    <UITooltipHelpIcon variant="normal" TooltipProps={{ placement: 'start' }}>
      <p className="text-sm w-80 whitespace-pre-wrap">
        {t('generation:sampling-method.help')}
      </p>
    </UITooltipHelpIcon>
  )

  return (
    <div className="flex gap-2 items-center">
      <SamplingMethodsSelectInner
        value={value}
        handleChange={handleChange}
        onChange={onChange}
        selectClassName={selectClassName}
        disabled={disabled}
        modelType={modelType}
      />
      {helpIcon}
    </div>
  )
}

export function CfgScaleSelectInner({
  value,
  handleChange,
  onChange,
  sliderClassName,
  lightningMode,
  disabled,
  minCfgScale = MIN_CFG_SCALE,
  maxCfgScale = MAX_CFG_SCALE,
}: {
  value?: number
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (value: number) => void
  sliderClassName?: string
  lightningMode?: boolean
  disabled?: boolean
  minCfgScale?: number
  maxCfgScale?: number
}) {
  const { t } = useTranslation(['generation'])

  return (
    <>
      {lightningMode && (
        <ControlSectionMessage
          className="my-2"
          type={ControlSectionMessageType.Warning}
        >
          {t('generation:cfg-scale.unavailable-in-lightning-mode')}
        </ControlSectionMessage>
      )}

      {value == null ? (
        <Spinner />
      ) : (
        <ValueSliderWithInput
          className="w-full"
          sliderClassName={cx('max-w-[400px]', sliderClassName)}
          disabled={disabled}
          value={value}
          onChange={value => {
            handleChange
              ? handleChange('cfgScale')(value)
              : onChange?.(value as number)
          }}
          min={minCfgScale}
          max={maxCfgScale}
          step={CFG_SCALE_STEP}
        />
      )}
    </>
  )
}

export function ShiftValueInner({
  value,
  handleChange,
  onChange,
  sliderClassName,
  disabled,
  minShiftValue = MIN_SHIFT_VALUE,
  maxShiftValue = MAX_SHIFT_VALUE,
  step = SHIFT_VALUE_STEP,
  className,
}: {
  value?: number
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (value: number) => void
  sliderClassName?: string
  disabled?: boolean
  minShiftValue?: number
  maxShiftValue?: number
  step?: number
  className?: string
}) {
  return (
    <>
      <ValueSliderWithInput
        className={className}
        min={minShiftValue}
        max={maxShiftValue}
        step={step}
        value={value}
        onChange={val =>
          handleChange
            ? handleChange('inferenceConfig.modelSampling.inputs.shift')(val)
            : onChange?.(val)
        }
        disabled={disabled}
        sliderClassName={sliderClassName}
      />
    </>
  )
}

export function CfgScaleSelect({
  value,
  handleChange,
  onChange,
  sliderClassName,
  lightningMode,
  disabled,
}: {
  value?: number
  handleChange?: HandleChange<keyof TaskSubmitFormValues>
  onChange?: (value: number) => void
  sliderClassName?: string
  lightningMode?: boolean
  disabled?: boolean
}) {
  const { t } = useTranslation(['generation'])

  const helpIcon = (
    <UITooltipHelpIcon variant="normal" TooltipProps={{ placement: 'start' }}>
      <p className="text-sm w-80 whitespace-pre-wrap">
        {t('generation:cfg-scale.help')}
      </p>
    </UITooltipHelpIcon>
  )
  return (
    <div>
      <div className="flex gap-2 items-center mb-2">
        <h2 className="text-[14px]">{t('generation:cfg-scale.label')}</h2>
        {helpIcon}
      </div>

      <CfgScaleSelectInner
        value={value}
        handleChange={handleChange}
        onChange={onChange}
        sliderClassName={sliderClassName}
        lightningMode={lightningMode}
        disabled={disabled}
      />
    </div>
  )
}
